/**
 * /setpieces — sibling surface to /tactics.
 *
 * Set pieces are routine-shaped (corners, FKs, throws, penalties — each
 * with an attacking + defending variant) so they live as their own
 * library distinct from the team's running formation + briefs in
 * /tactics. The library is read from `setPiecesStore` (localStorage,
 * v1) — same source the in-board SetPieceDrawer reads from.
 *
 * v1 page surface:
 *   - 8 type tabs across the top (corner-att, corner-def, FK-att,
 *     FK-def, throw-att, throw-def, pen-att, pen-def)
 *   - list of saved routines per type with Apply (→ /2d) + Delete
 *   - an empty-state CTA to "Create on the Tactics board" which deep-links
 *     to /2d where the SetPieceDrawer "New from current" path takes over
 *
 * Editing the routine's positions / arrows still happens on the Tactics
 * board itself (the canvas + drag stack lives there). This page is the
 * coach's home base for *finding* and *applying* routines.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SET_PIECE_TYPES,
  type SetPieceType,
  type TacticsSetPiece,
} from '@4cm/core/tactics/setPieces';
import {
  CORNER_TEMPLATES,
  type SetPieceTemplate,
} from '@4cm/core/tactics/cornerTemplates';
import { deleteSetPiece, listSetPieces, saveSetPiece } from '../lib/setPiecesStore';
import './SetPieces.css';

const TYPE_LABELS: Record<SetPieceType, string> = {
  'corner-att':   'Corner ▶',
  'corner-def':   'Corner ◀',
  'freekick-att': 'FK ▶',
  'freekick-def': 'FK ◀',
  'throwin-att':  'Throw ▶',
  'throwin-def':  'Throw ◀',
  'penalty-att':  'Pen ▶',
  'penalty-def':  'Pen ◀',
};

const TYPE_DESCRIPTIONS: Record<SetPieceType, string> = {
  'corner-att':   'Attacking corners — near-post runs, screens, second-ball traps',
  'corner-def':   'Defending corners — zonal, man, hybrid markers',
  'freekick-att': 'Attacking free kicks — direct, wall-bender, blocked',
  'freekick-def': 'Defending free kicks — wall setup, runner picks',
  'throwin-att':  'Attacking throw-ins — long throws, short-3rd combinations',
  'throwin-def':  'Defending throw-ins — press triggers, mark assignments',
  'penalty-att':  'Penalties — taker, follow-up runners',
  'penalty-def':  'Defending penalties — GK cues, rebound coverage',
};

export function SetPieces() {
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState<SetPieceType>('corner-att');
  const [pieces, setPieces] = useState<TacticsSetPiece[]>(() => listSetPieces());

  const refresh = () => setPieces(listSetPieces());

  const filtered = useMemo(
    () => pieces.filter((p) => p.type === activeType),
    [pieces, activeType],
  );

  const handleDelete = (id: string) => {
    deleteSetPiece(id);
    refresh();
  };

  const handleApply = (piece: TacticsSetPiece) => {
    // Deep-link into the 2D Tactics board with this piece's id so the
    // board can pull it from setPiecesStore on mount and snap to it.
    navigate(`/2d?setpiece=${encodeURIComponent(piece.id)}`);
  };

  const handleCreate = () => {
    // The "new from current" capture lives inside the Tactics board's
    // SetPieceDrawer footer — send the coach there with the active
    // type pre-selected.
    navigate(`/2d?openSetPieces=1&type=${encodeURIComponent(activeType)}`);
  };

  // Built-in starter routines for the active type. Importing copies one
  // into the coach's own library (the store mints id + createdAt).
  const templates = useMemo(
    () => CORNER_TEMPLATES.filter((t) => t.type === activeType),
    [activeType],
  );

  const handleImport = (template: SetPieceTemplate) => {
    saveSetPiece(template);
    refresh();
  };

  return (
    <main className="setpieces-page">
      <header className="setpieces-page__header">
        <div>
          <h1 className="setpieces-page__title">Set Pieces</h1>
          <p className="setpieces-page__subtitle">
            Recallable routines — corners, free kicks, throws, penalties.
            Open one on the Tactics board to edit positions and runs.
          </p>
        </div>
        <button
          type="button"
          className="setpieces-page__create"
          onClick={handleCreate}
        >
          + Create {TYPE_LABELS[activeType]}
        </button>
      </header>

      <nav className="setpieces-page__tabs" role="tablist" aria-label="Set-piece type">
        {SET_PIECE_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={activeType === t}
            className="setpieces-page__tab"
            data-active={activeType === t}
            onClick={() => setActiveType(t)}
          >
            <span className="setpieces-page__tab-label">{TYPE_LABELS[t]}</span>
            <span className="setpieces-page__tab-count">
              {pieces.filter((p) => p.type === t).length}
            </span>
          </button>
        ))}
      </nav>

      <p className="setpieces-page__type-blurb">{TYPE_DESCRIPTIONS[activeType]}</p>

      {templates.length > 0 && (
        <section
          className="setpieces-page__templates"
          aria-label="Starter templates"
        >
          <h2 className="setpieces-page__templates-title">Starter templates</h2>
          <p className="setpieces-page__templates-hint">
            Ready-made routines — import one to your library, then edit it
            on the Tactics board. Players keep their own numbers.
          </p>
          <div className="setpieces-page__template-grid">
            {templates.map((t) => (
              <article key={t.name} className="setpieces-page__template">
                <h3 className="setpieces-page__template-name">{t.name}</h3>
                <div className="setpieces-page__template-meta">
                  {t.delivery && <span data-kind="delivery">{t.delivery}</span>}
                  <span data-kind="arrows">
                    {t.arrows.length} run{t.arrows.length === 1 ? '' : 's'}
                  </span>
                </div>
                <button
                  type="button"
                  className="setpieces-page__template-import"
                  onClick={() => handleImport(t)}
                  aria-label={`Import ${t.name} to your library`}
                >
                  + Import
                </button>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="setpieces-page__list" role="list">
        {filtered.length === 0 && (
          <div className="setpieces-page__empty">
            <p>
              No saved {TYPE_LABELS[activeType].toLowerCase()} routines yet.
            </p>
            <button
              type="button"
              className="setpieces-page__empty-cta"
              onClick={handleCreate}
            >
              Build one on the Tactics board →
            </button>
          </div>
        )}
        {filtered.map((p) => (
          <article key={p.id} className="setpieces-page__row" role="listitem">
            <div className="setpieces-page__row-body">
              <h3 className="setpieces-page__row-title">{p.name}</h3>
              <div className="setpieces-page__row-meta">
                {p.delivery && <span data-kind="delivery">{p.delivery}</span>}
                <span data-kind="arrows">
                  {p.arrows.length} run{p.arrows.length === 1 ? '' : 's'}
                </span>
                <span data-kind="created">
                  {new Date(p.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="setpieces-page__row-actions">
              <button
                type="button"
                className="setpieces-page__row-apply"
                onClick={() => handleApply(p)}
                aria-label={`Open ${p.name} on the Tactics board`}
              >
                Open
              </button>
              <button
                type="button"
                className="setpieces-page__row-delete"
                onClick={() => handleDelete(p.id)}
                aria-label={`Delete set piece ${p.name}`}
              >
                Delete
              </button>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}
